import clsx from 'clsx';
import { useToast } from 'components/ToastManager';
import { useGetWorkspaceMembersQuery } from 'hooks/globalApi';
import {
  useCreatePlatformOnboardingApplicationMutation,
  useGetPaymentPlatformEntityQuery,
} from 'hooks/internalApi';
import { useGlobalApiContext } from 'hooks/useGlobalApiContext';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import React, { useCallback } from 'react';
import { match } from 'ts-pattern';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';

import {
  type LegalEntityStatus,
  LegalEntityStatusSchema,
} from '@fragment/billing/platform/entitySchemas';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { ErrorText } from '@fragment/ui/src/components/ErrorText';
import { LoadingText } from '@fragment/ui/src/components/LoadingText';
import { Toast } from '@fragment/ui/src/components/Toast';

const statusToColor = (status: LegalEntityStatus) =>
  match(status)
    .with(
      LegalEntityStatusSchema.Enum['pending-submission'],
      () => 'border-[0.5px] border-main-300 bg-transparent'
    )
    .with(
      LegalEntityStatusSchema.Enum['pending-approval'],
      () => 'bg-expense-yellow'
    )
    .with(LegalEntityStatusSchema.Enum.approved, () => 'bg-asset-green')
    .with(LegalEntityStatusSchema.Enum.rejected, () => 'bg-liability-red')
    .with(
      LegalEntityStatusSchema.Enum['manual-review'],
      () => 'bg-liability-red'
    )
    .exhaustive();

const statusToMetaText = (status: LegalEntityStatus) =>
  match(status)
    .with(
      LegalEntityStatusSchema.Enum['pending-submission'],
      () => 'Incomplete'
    )
    .with(
      LegalEntityStatusSchema.Enum['pending-approval'],
      () => 'Submitted for review'
    )
    .with(LegalEntityStatusSchema.Enum.approved, () => 'Approved')
    .with(LegalEntityStatusSchema.Enum.rejected, () => 'Rejected')
    .with(
      LegalEntityStatusSchema.Enum['manual-review'],
      () => 'Sent for manual review'
    )
    .exhaustive();

const StatusList = ({
  label,
  slots,
  onClick,
}: {
  label: string;
  slots: readonly {
    key: string;
    title: string;
    status: LegalEntityStatus;
  }[];
  onClick?: () => void;
}) => (
  <div className="flex flex-col h-fit text-xmd">
    <span className="text-main">{label}</span>
    {slots.length === 0 && (
      <div className="flex text-main-500 h-f4 min-h-f2 pl-[1ch] border-main-300 border-[0.5px] items-start">
        <div className="flex items-center">
          <div
            className={clsx(
              `w-[1ch] h-[1ch] rounded-full border-[0.5px] border-main-300`,
              'bg-transparent'
            )}
          />
          <span className="text-main-500 pl-[1ch]">None Invited</span>
        </div>
      </div>
    )}
    {slots.map((slot, index) => (
      <button
        type="button"
        onClick={onClick}
        key={slot.key}
        className={clsx(
          `flex flex-col border-main-300 border-[0.5px] px-[1ch] justify-start items-start`,
          index > 0 && 'border-t-0',
          onClick && 'hover:bg-negative'
        )}
      >
        <div className="flex flex-row items-center gap-x-[1ch]">
          <div
            className={clsx(
              `w-[1ch] h-[1ch] rounded-full`,
              statusToColor(slot.status)
            )}
          />
          <span className="text-main">{slot.title}</span>
        </div>
        <span className="text-main-500 pl-[2ch]">
          {statusToMetaText(slot.status)}
        </span>
      </button>
    ))}
  </div>
);

export const ApplicationStatusView = () => {
  const workspaceId = useWorkspaceId();
  const { context } = useInternalApiContext();
  const { context: globalContext } = useGlobalApiContext();
  const { showToast } = useToast();
  const [, setLocation] = useLocation();

  // get workspace members to show user email addresses associated with user IDs
  const [
    {
      data: workspaceData,
      error: workspaceQueryError,
      fetching: fetchingWorkspaceMembers,
    },
  ] = useGetWorkspaceMembersQuery({
    variables: { workspaceId },
    context: globalContext,
  });

  const { result: workspaceResult, isError: workspaceError } = getResultOrError(
    workspaceData?.workspace,
    workspaceQueryError
  );
  const { result: members, isError: membersError } = getResultOrError(
    workspaceResult?.members,
    workspaceQueryError
  );

  const [
    {
      data: paymentData,
      error: paymentQueryError,
      fetching: fetchingPaymentPlatform,
    },
  ] = useGetPaymentPlatformEntityQuery({
    variables: { workspaceId },
    context,
  });

  const rawPaymentEntity = paymentData?.paymentPlatformEntity;
  const paymentParsed = getResultOrError(rawPaymentEntity, paymentQueryError);
  const paymentNotFound =
    rawPaymentEntity?.__typename === 'NotFoundError' ||
    (paymentParsed.isError &&
      paymentParsed.errorMessage.toLowerCase().includes('not found'));

  // Handle creating a new platform application or continuing the existing one
  const [
    { fetching: creatingApplication },
    createPlatformOnboardingApplication,
  ] = useCreatePlatformOnboardingApplicationMutation();

  const handleCreate = useCallback(async () => {
    const creationResult = await createPlatformOnboardingApplication(
      { workspaceId },
      context
    );
    const { isError, errorMessage } = getResultOrError(
      creationResult.data?.createPlatformOnboardingApplication,
      creationResult.error
    );
    if (isError) {
      showToast(<Toast message={errorMessage} type="error" />);
    } else {
      showToast(<Toast message="Application Started" type="success" />);
      setLocation(
        makePath(['w', workspaceId, 'onboarding'], { absolute: true })
      );
    }
  }, [
    workspaceId,
    createPlatformOnboardingApplication,
    context,
    showToast,
    setLocation,
  ]);

  const handleContinue = useCallback(() => {
    setLocation(makePath(['w', workspaceId, 'onboarding'], { absolute: true }));
  }, [workspaceId, setLocation]);

  if (fetchingPaymentPlatform || fetchingWorkspaceMembers) {
    return <LoadingText text="Loading Application" />;
  }

  if (workspaceError || membersError) {
    return (
      <ErrorText>
        Failed to load workspace members, please refresh the page.
      </ErrorText>
    );
  }

  if (paymentParsed.isError && paymentNotFound) {
    return (
      <div className="flex flex-col gap-y-f2 text-xmd">
        <span className="text-main">Platform Onboarding</span>
        <span className="text-main-500">
          Complete the necessary onboarding steps to enable Fragment Payments.
        </span>
        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered */}
        <PrimaryButtonV2 onClick={handleCreate} disabled={creatingApplication}>
          Start
        </PrimaryButtonV2>
      </div>
    );
  }

  if (paymentParsed.isError) {
    return (
      <ErrorText>
        Failed to load application status, please refresh the page.
      </ErrorText>
    );
  }

  const { platformEntity } = paymentParsed.result;
  const controllers = platformEntity.controllers ?? [];
  const owners = platformEntity.owners ?? [];

  const validatedStatuses = (() => {
    try {
      const businessStatus = LegalEntityStatusSchema.parse(
        platformEntity.status
      );
      const controllerSlots = controllers.map((c) => {
        const matchingMember = members.nodes.find(
          (m) => m?.user.id === c.userId
        );
        return {
          title: matchingMember?.user.email ?? '',
          status: LegalEntityStatusSchema.parse(c.status),
          key: `controller-${c.userId ?? c.id}`,
        };
      });
      const ownerSlots = owners.map((o) => {
        const matchingMember = members.nodes.find(
          (m) => m?.user.id === o.userId
        );
        return {
          title: matchingMember?.user.email ?? '',
          status: LegalEntityStatusSchema.parse(o.status),
          key: `owner-${o.userId ?? o.id}`,
        };
      });
      return {
        ok: true as const,
        businessStatus,
        controllerSlots,
        ownerSlots,
      };
    } catch {
      return { ok: false as const };
    }
  })();

  if (!validatedStatuses.ok) {
    return (
      <ErrorText>
        Application data is invalid or out of date. Please refresh the page.
      </ErrorText>
    );
  }

  const { businessStatus, controllerSlots, ownerSlots } = validatedStatuses;

  return (
    <div className="flex flex-col text-xmd gap-y-f2 ">
      <div className="flex flex-col gap-y-f2">
        <StatusList
          label="Platform Application"
          slots={[
            {
              title: 'Questionnaire',
              status: businessStatus,
              key: 'business-application',
            },
          ]}
          onClick={
            businessStatus ===
            LegalEntityStatusSchema.Enum['pending-submission']
              ? handleContinue
              : undefined
          }
        />

        <StatusList label="Controllers" slots={controllerSlots} />
        <StatusList label="Owners" slots={ownerSlots} />
      </div>
    </div>
  );
};
